import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Logo } from "@/components/Logo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.paymentStatus(sessionId);
        if (cancelled) return;
        if (res.payment_status === "paid") {
          setStatus("paid");
          setOrder(res.order);
          clear();
          return;
        }
        attempts.current += 1;
        if (attempts.current < 10) setTimeout(poll, 2000);
        else setStatus("pending");
      } catch {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  return (
    <div className="min-h-screen bg-[#FAF5EE] hex-pattern flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-white rounded-2xl border border-[#E6D7C3] p-10 text-center shadow-xl">
        <div className="flex justify-center mb-6"><Logo size={48} /></div>
        {status === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-10 h-10 text-[#D97706] animate-spin mx-auto" />
            <h1 className="mt-5 font-serif-display text-2xl font-bold text-[#2C221E]">Confirming your order...</h1>
            <p className="mt-2 text-sm text-[#8C7A6B]">Just a moment while we check with the hive.</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="payment-confirmed">
            <div className="w-14 h-14 rounded-full bg-[#2D4A3E] mx-auto flex items-center justify-center">
              <Check className="w-7 h-7 text-[#FAF5EE]" />
            </div>
            <h1 className="mt-5 font-serif-display text-2xl font-bold text-[#2C221E]">Thank you kindly!</h1>
            <p className="mt-3 text-sm text-[#5A4D44] leading-relaxed">
              Your order is confirmed. The family will hand-wrap your goods and get them on their way.
            </p>
            {order && (
              <div className="mt-6 bg-[#F3EBE0] rounded-xl p-5 text-left" data-testid="order-summary">
                {order.items?.map((i) => (
                  <div key={i.product_id} className="flex justify-between text-sm text-[#5A4D44] py-1">
                    <span>{i.name} × {i.quantity}</span>
                    <span>${(i.price * i.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm font-bold text-[#2C221E] pt-2 mt-2 border-t border-[#E6D7C3]">
                  <span>Total</span>
                  <span data-testid="order-total">${order.total?.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        )}
        {(status === "pending" || status === "error") && (
          <div data-testid="payment-pending">
            <h1 className="font-serif-display text-2xl font-bold text-[#2C221E]">We're still checking on that</h1>
            <p className="mt-3 text-sm text-[#5A4D44] leading-relaxed">
              Your payment is being confirmed. If you completed checkout, your order will go through — feel free to reach out via the contact form with any questions.
            </p>
          </div>
        )}
        <Link
          to="/"
          data-testid="back-home-link"
          className="mt-8 inline-block px-8 py-3 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white font-semibold text-sm transition-colors"
        >
          Back to the Hive
        </Link>
      </div>
    </div>
  );
}
